import { Component, OnInit, Inject } from '@angular/core';
import { MdDialogRef, MD_DIALOG_DATA } from '@angular/material';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs/Observable';
import { ObservableRequestService } from 'app/services/observable-request.service';
import { ConstantsService } from 'app/services/constants-service';
import { FinanceService } from 'app/finance/finance.service';
import { SnackBarService } from 'app/services/snackbar.service';
import { WarmUpService, ApplicationTypeConstants } from 'app/services/warmup/warmup.service';
import { MessageService } from 'app/services/messages.service';
import { GenericSearchDetails } from 'app/shared/models/generic-search-details.model';
import { Headers } from '@angular/http';

@Component({
  selector: 'app-create-account-dialog',
  templateUrl: './create-account-dialog.component.html',
  styleUrls: ['./create-account-dialog.component.css']
})
export class CreateAccountDialogComponent implements OnInit {
  createAccountForm: FormGroup;
  selectedClient: GenericSearchDetails = null;
  clientSearchURI = ConstantsService.CLIENT_SEARCH;
  clientSearchPlaceholder = MessageService.CLIENT_NAME_SEARCH_PLACEHOLDER;
  creatingAccount = false;
  bankInstitutions: any[] = [];
  currencies: any[] = [];

  constructor(
    public dialogRef: MdDialogRef<CreateAccountDialogComponent>,
    @Inject(MD_DIALOG_DATA) public data: any,
    private formBuilder: FormBuilder,
    private requestService: ObservableRequestService,
    private financeService: FinanceService,
    private snackBarService: SnackBarService,
    private warmUpService: WarmUpService
  ) {
    this.createAccountForm = this.formBuilder.group({
      clientName: ['', Validators.required],
      accountName: ['', Validators.required],
      accountNumber: [{value: '', disabled: true}, Validators.required],
      bankInstitutionId: ['', Validators.required],
      currencyId: ['', Validators.required]
    });
  }

  ngOnInit() {
    // Pre-fill account number from bank statement
    if (this.data && this.data.accountNumber) {
      this.createAccountForm.patchValue({
        accountNumber: this.data.accountNumber
      });
    }

    // Pre-fill client name if already matched
    if (this.data && this.data.clientId && this.data.clientName) {
      this.createAccountForm.patchValue({
        clientName: this.data.clientName
      });
    }

    // Load bank institutions
    this.loadBankInstitutions();

    // Load currencies
    this.loadCurrencies();
  }

  onClientSelection(searchDetails: GenericSearchDetails) {
    this.selectedClient = searchDetails;
    this.createAccountForm.patchValue({
      clientName: searchDetails.clientName,
      accountName: searchDetails.clientName // Auto-fill account name with client name
    });
  }

  onClientNameChanged(value: string) {
    // Clear selected client if user manually changes the name
    if (!value || typeof value !== 'string') {
      this.selectedClient = null;
      this.createAccountForm.get('clientName').setErrors({ 'invalid': true });
    } else {
      this.createAccountForm.get('clientName').setErrors(null);
    }
  }

  loadBankInstitutions() {
    this.financeService.getBankInstitutionsList().subscribe(
      (institutions: any) => {
        this.bankInstitutions = Array.isArray(institutions) ? institutions : [];
      },
      (error: any) => {
        console.error('Error loading bank institutions: ' + error.message);
      }
    );
  }

  loadCurrencies() {
    // Get currencies from warmup service (same as BankAccountComponent)
    const currencyValues = this.warmUpService.getApplicationValue(ApplicationTypeConstants.CURRENCY);
    if (currencyValues instanceof Observable) {
      currencyValues.subscribe(
        (response: any) => {
          this.warmUpService.setApplicationTypeInLS(response);
          this.currencies = response.values;
        }
      );
    } else if (currencyValues && currencyValues.length > 0) {
      this.currencies = currencyValues;
    } else {
      // Fallback: try to load from API
      this.requestService.getRequest('/application-values/Currency').subscribe(
        (response: any) => {
          if (response && response.arrResult) {
            this.currencies = response.arrResult;
          } else if (Array.isArray(response)) {
            this.currencies = response;
          }
        },
        (error: any) => {
          console.error('Error loading currencies: ' + error.message);
          // Default to EUR if currency loading fails
          this.currencies = [{ id: 1, value: 'EUR', description: 'Euro' }];
        }
      );
    }
  }


  onCancel() {
    this.dialogRef.close();
  }

  onCreateAccount() {
    if (!this.selectedClient || !this.selectedClient.entityId) {
      this.snackBarService.showSimpleMsj('Please select a valid client from the search results');
      this.createAccountForm.get('clientName').setErrors({ 'invalid': true });
      this.createAccountForm.get('clientName').markAsTouched();
      return;
    }

    if (this.createAccountForm.valid) {
      this.creatingAccount = true;
      const formValue = this.createAccountForm.getRawValue();
      
      // Find the bank institution object to get bankId and bankName
      const selectedBank = this.bankInstitutions.find(b => 
        (b.bankId && b.bankId === formValue.bankInstitutionId) || 
        (b.id && b.id === formValue.bankInstitutionId)
      );
      
      const accountData = {
        accountName: formValue.accountName,
        accountNumber: formValue.accountNumber,
        bankInstitutionId: selectedBank ? (selectedBank.bankId || selectedBank.id) : formValue.bankInstitutionId,
        bankInstitutionName: selectedBank ? selectedBank.bankName : '',
        currencyId: formValue.currencyId,
        accountHolderEntityId: this.selectedClient.entityId
      };
      
      console.log('Creating bank account with data:', accountData);

      this.financeService.saveBankAccount(accountData).subscribe(
        (response: any) => {
          this.creatingAccount = false;
          if (response && response.flagResult) {
            // Account created successfully
            // The response might contain bankAccountId or we need to extract it from the response
            let bankAccountId = null;
            if (response.bankAccountId) {
              bankAccountId = response.bankAccountId;
            } else if (response.arrResult && response.arrResult.length > 0 && response.arrResult[0].accountId) {
              bankAccountId = response.arrResult[0].accountId;
            } else if (response.accountId) {
              bankAccountId = response.accountId;
            }
            
            this.dialogRef.close({
              success: true,
              clientId: this.selectedClient.clientId,
              bankAccountId: bankAccountId
            });
          } else {
            this.snackBarService.showSimpleMsj('Account created but could not determine account ID');
            this.dialogRef.close({
              success: true,
              clientId: this.selectedClient.clientId,
              bankAccountId: null
            });
          }
        },
        (error: any) => {
          this.creatingAccount = false;
          this.snackBarService.showSimpleMsj('Error creating account: ' + (error.message || 'Unknown error'));
          console.error('Error creating bank account: ' + error.message);
        }
      );
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.createAccountForm.controls).forEach(key => {
        const control = this.createAccountForm.get(key);
        if (control) {
          control.markAsTouched();
        }
      });
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/finance/bank-statements-batch/create-account-dialog/create-account-dialog.component.ts